import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { useLocationsState } from '../../contexts';
import { NewLocationModal } from '../NewLocationModal';
import type { Location } from '../../api/Locations/types';

interface LocationSelectorProps {
  value: string;
  onChange: (value: string) => void;
  onError: (message: string) => void;
  placeholder?: string;
  className?: string;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  onError,
  placeholder = 'Select a location',
  className = 'w-full',
}) => {
  const { locations, fetchLocations } = useLocationsState();
  const [isNewLocationModalOpen, setIsNewLocationModalOpen] = useState(false);
  const [newlyCreatedLocation, setNewlyCreatedLocation] = useState<Location | null>(null);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === '__new__') {
      setIsNewLocationModalOpen(true);
    } else {
      onChange(selectedValue);
    }
  };

  const handleLocationCreated = (location: Location) => {
    setNewlyCreatedLocation(location);
    onChange(location.uid);
  };

  return (
    <>
      <Select key={newlyCreatedLocation?.uid || 'location-select'} value={value} onValueChange={handleValueChange}>
        <SelectTrigger className={`py-2.5 ${className}`} size="sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__new__" className="text-primary-600 font-medium">
            + New Location
          </SelectItem>
          {newlyCreatedLocation && (
            <SelectItem key={newlyCreatedLocation.uid} value={newlyCreatedLocation.uid}>
              {newlyCreatedLocation.displayName}
            </SelectItem>
          )}
          {locations.map((loc) => (
            <SelectItem key={loc.uid} value={loc.uid}>
              {loc.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <NewLocationModal
        isOpen={isNewLocationModalOpen}
        onClose={() => setIsNewLocationModalOpen(false)}
        onCreated={handleLocationCreated}
        onError={onError}
      />
    </>
  );
};
